// ── Tlhakiso Stokvel — bank statement import (PDF / CSV) ──
//
// Turns a bank statement the group exports from its OWN account into classified
// money-in / money-out rows, and auto-matches each deposit to a member by their
// payment reference — so the treasurer reconciles a whole statement in one review
// instead of typing every line.
//
// NON-CUSTODIAL: this only READS a statement and PROPOSES records for the
// treasurer to confirm. No money moves; nothing is written until they click.
//
// Reuses the SAME engine as the main Tlhakiso reconciliation platform:
//   • CSV / TXT → parseBankCsv           (deterministic column-sniffing, no AI cost)
//   • PDF       → extractPdfText          (server-side text layer, lossless)
//                 → extractRowsFromStatementText (Claude transcription into rows)
// Both yield one shape: { date, description, amount, balance } where `amount` is
// SIGNED — positive = money IN, negative = money OUT.

import { parseBankCsv } from './csvParser';
import { extractPdfText } from './pdfText';
import { extractRowsFromStatementText } from './extractJournals';
import { matchTxnToMember, memberKey, normRef } from './stokvelHelpers';

function readAsText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('Could not read ' + (file && file.name)));
    r.readAsText(file);
  });
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const comma = s.indexOf(','); // strip the "data:...;base64," prefix
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.onerror = () => reject(new Error('Could not read ' + (file && file.name)));
    r.readAsDataURL(file);
  });
}

// Turn an uploaded statement file into normalised, signed rows.
// onProgress(message) is called at each step so the UI can show what's happening.
export async function parseStatementFile(file, onProgress) {
  const name = (file && file.name) || 'statement';
  const lower = name.toLowerCase();
  const say = (m) => { if (onProgress) onProgress(m); };

  if (lower.endsWith('.csv') || lower.endsWith('.txt')) {
    say('Reading ' + name + '…');
    const text = await readAsText(file);
    const { rows, error } = parseBankCsv(text);
    if (error) throw new Error(error);
    if (!rows.length) throw new Error('No transactions found in ' + name + '. Make sure it is a bank statement export (Date + Amount/Debit/Credit columns).');
    return { rows, source: name, method: 'csv' };
  }

  if (lower.endsWith('.pdf')) {
    say('Uploading ' + name + '…');
    const b64 = await readAsBase64(file);
    say('Extracting text from the PDF…');
    const { text } = await extractPdfText(b64);
    if (!text || text.replace(/\s/g, '').length < 40) {
      throw new Error('This PDF has no readable text layer — it looks scanned or photographed. Export the statement as CSV from your banking app and upload that instead.');
    }
    // DETERMINISTIC-FIRST: try to read the rows with plain code (free, no AI).
    // Only accepted when confident and UNAMBIGUOUS — the moment lines look like
    // they carry an amount AND a running balance (which plain code can confuse),
    // we bail to the AI transcription rather than risk crediting the wrong number.
    const det = deterministicRows(text);
    if (det) {
      return { rows: det, source: name, method: 'pdf-text' };
    }
    say('Reading transactions with the extraction engine…');
    const { rows } = await extractRowsFromStatementText({ text, sourceLabel: name });
    if (!rows || !rows.length) throw new Error('No transactions could be read from ' + name + '.');
    return { rows, source: name, method: 'pdf-ai' };
  }

  throw new Error('Unsupported file type. Upload a PDF or CSV bank statement.');
}

// ── Deterministic (no-AI) parse of a PDF text layer ──
// Returns normalised signed rows, or NULL to signal "not confident — use AI".
// Conservative by design: for a money tool a cheap-but-wrong parse is worse than
// a few cents, so this only returns rows when the layout is unambiguous.
const DATE_RE = /\b(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}|\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{2,4})\b/i;
// A money token: optional sign/paren/R, then an integer part that is EITHER
// strictly-grouped thousands ("1,200" / "12 500" / "1 234 567") OR plain digits
// ("1200"), followed by REQUIRED .dd decimals. Using strict 3-digit groups (not a
// loose "digits and spaces" class) stops a reference number's trailing digits from
// bridging across whitespace into the amount (e.g. "STK03  1200.00" → 1200, not
// 31200). The required decimals keep it from matching reference/account numbers.
const MONEY_RE = /-?\(?\s*R?\s*(?:\d{1,3}(?:[ ,]\d{3})+|\d+)\.\d{2}\s*\)?-?/g;
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function pad2(s) { s = String(s); return s.length < 2 ? '0' + s : s; }

function isoDate(s) {
  const t = String(s).trim();
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return m[1] + '-' + pad2(m[2]) + '-' + pad2(m[3]);
  m = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/);
  if (m) { let y = m[3]; if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y; return y + '-' + pad2(m[2]) + '-' + pad2(m[1]); }
  m = t.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{2,4})/);
  if (m) { const mi = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase()); if (mi >= 0) { let y = m[3]; if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y; return y + '-' + pad2(String(mi + 1)) + '-' + pad2(m[1]); } }
  return null;
}

function moneyNum(tok) {
  let t = String(tok).replace(/[R\s]/g, '');
  let neg = false;
  if (/^\(.*\)$/.test(t)) { neg = true; t = t.slice(1, -1); }
  if (/^-/.test(t) || /-$/.test(t)) { neg = true; t = t.replace(/-/g, ''); }
  t = t.replace(/,/g, '');
  const n = parseFloat(t);
  if (isNaN(n)) return null;
  return neg ? -n : n;
}

function deterministicRows(text) {
  const lines = String(text).split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const dateLines = [];
  let multiMoneyLines = 0; // lines with 2+ money tokens = amount+balance ambiguity

  for (const line of lines) {
    if (!DATE_RE.test(line)) continue;
    if (/opening balance|closing balance|brought forward|balance b\/f|balance c\/f|statement|page \d/i.test(line)) continue;
    const monies = line.match(MONEY_RE) || [];
    dateLines.push({ line, monies });
    if (monies.length >= 2) multiMoneyLines++;
  }

  // Confidence gates — fail any and we hand off to the AI:
  if (dateLines.length < 3) return null;                       // too little to trust
  const withOneMoney = dateLines.filter((d) => d.monies.length === 1);
  // If a meaningful share of lines carry 2+ amounts (amount + running balance),
  // plain code can't reliably tell them apart → bail to AI.
  if (multiMoneyLines / dateLines.length > 0.1) return null;
  // Require the vast majority of date lines to have exactly one clean amount.
  if (withOneMoney.length / dateLines.length < 0.85) return null;

  const rows = [];
  for (const { line, monies } of dateLines) {
    if (monies.length !== 1) continue;
    const dm = line.match(DATE_RE);
    const date = dm ? isoDate(dm[0]) : null;
    const amount = moneyNum(monies[0]);
    if (!date || amount === null || amount === 0) return null; // any bad row → don't trust the whole parse
    // Description = the line with the date and amount stripped out.
    const description = line.replace(dm[0], '').replace(monies[0], '').replace(/\s{2,}/g, ' ').trim();
    rows.push({ date, description, amount, balance: null });
  }
  if (rows.length < 3) return null;
  return rows;
}

// The transaction types a statement line can become. `dir` gates which types a
// row may take (a credit can't be an expense, a debit can't be a contribution).
export const IMPORT_TYPES = [
  { id: 'contribution', label: 'Contribution',    dir: 'in',  member: true },
  { id: 'repayment',    label: 'Loan repayment',  dir: 'in',  member: true },
  { id: 'income',       label: 'Interest / income', dir: 'in', member: false },
  { id: 'loanOut',      label: 'Loan disbursed',  dir: 'out', member: true },
  { id: 'expense',      label: 'Expense / fee',   dir: 'out', member: false },
  { id: 'payout',       label: 'Payout',          dir: 'out', member: true },
  { id: 'skip',         label: 'Skip',            dir: 'any', member: false },
];

// Bracket markers the chairperson asks members to use, + bank-description keywords.
const RE_REPAYMENT = /\(\s*loan\s*repay(?:ment)?\s*\)/i;   // "(loan repayment)" on a credit
const RE_LOAN_OUT  = /\(\s*loan\s*\)/i;                     // "(loan)" on a debit
const RE_BANK_FEE  = /\b(bank\s*charge|service\s*(?:fee|charge)|admin\s*fee|monthly\s*(?:account\s*)?fee|debit\s*interest|transaction\s*fee|fee|charges?)\b/i;
const RE_INTEREST_IN = /\b(credit\s*interest|interest\s*(?:earned|received|cr)|interest)\b/i;

function fingerprint(kind, mk, date, amount) {
  return kind + '|' + (mk || '') + '|' + (date || '') + '|' + (Number(amount) || 0).toFixed(2);
}

// Build the set of fingerprints already in the books so a re-imported statement
// can't double-count — across every type, not just contributions.
function existingFingerprints(data) {
  const s = new Set();
  (data && data.contributions || []).filter((c) => c.status !== 'void').forEach((c) => s.add(fingerprint('contribution', memberKey(c), c.date, c.amount)));
  (data && data.repayments || []).forEach((r) => s.add(fingerprint('repayment', r.memberUid ? r.memberUid : 'ref:' + normRef(r.reference || ''), r.date, r.amount)));
  (data && data.expenses || []).forEach((e) => s.add(fingerprint('expense', '', e.date, e.amount)));
  (data && data.income || []).forEach((i) => s.add(fingerprint('income', '', i.date, i.amount)));
  (data && data.loans || []).forEach((l) => s.add(fingerprint('loanOut', l.memberUid ? l.memberUid : 'ref:' + normRef(l.reference || ''), l.issueDate, l.principal)));
  (data && data.payouts || []).forEach((p) => s.add(fingerprint('payout', '', p.date, p.amount)));
  return s;
}

// The member's single active loan (to auto-link a repayment). Returns the loanId,
// or null if they have none or more than one (chair resolves the latter later).
function activeLoanForMember(loans, repayments, memberUid, reference) {
  const mine = (loans || []).filter((l) => {
    if (l.status === 'written_off') return false;
    if (memberUid && l.memberUid === memberUid) return true;
    if (!memberUid && reference && normRef(l.reference) === normRef(reference)) return true;
    return false;
  });
  const active = mine.filter((l) => {
    const repaid = (repayments || []).filter((r) => r.loanId === l.id).reduce((s, r) => s + (Number(r.amount) || 0), 0);
    return repaid < (Number(l.principal) || 0) * 1.0 + 999999; // any not-obviously-settled loan
  });
  return active.length === 1 ? active[0].id : (mine.length === 1 ? mine[0].id : null);
}

// Classify signed rows into typed, categorised import rows. Detects the
// (loan)/(loan repayment) markers and bank fee/interest keywords, matches members
// by reference, links repayments to the member's active loan, and flags duplicates
// against everything already recorded. Every row carries an editable `type` so the
// chairperson can re-route anything the auto-classifier got wrong.
export function classifyStatement(rows, stokvel, data) {
  const members = (stokvel && stokvel.members) || {};
  const loans = (data && data.loans) || [];
  const repayments = (data && data.repayments) || [];
  const existing = existingFingerprints(data);
  const batch = new Set();
  const out = [];

  (rows || []).forEach((r, i) => {
    const signed = Number(r.amount) || 0;
    if (signed === 0) return;
    const date = r.date || '';
    const description = r.description || '';
    const amount = Math.abs(signed);
    const dir = signed > 0 ? 'in' : 'out';
    const key = matchTxnToMember({ amount, description }, members);
    const m = key ? (members[key] || {}) : null;
    const memberUid = key && !key.startsWith('tracked:') ? key : null;
    const reference = m ? (m.reference || '') : '';

    let type = 'skip';
    let category = '';
    let loanId = null;

    if (dir === 'in') {
      if (key && RE_REPAYMENT.test(description)) {
        type = 'repayment';
        loanId = activeLoanForMember(loans, repayments, memberUid, reference);
        if (!loanId) type = 'contribution'; // no loan to repay → treat as contribution
      } else if (!key && RE_INTEREST_IN.test(description)) {
        type = 'income'; category = 'interest_earned';
      } else if (key) {
        type = 'contribution';
      } else {
        type = 'skip'; // unmatched credit — chair assigns a member/type
      }
    } else { // out
      if (key && RE_LOAN_OUT.test(description)) {
        type = 'loanOut';
      } else if (RE_BANK_FEE.test(description)) {
        type = 'expense'; category = 'bank_charge';
      } else {
        type = 'expense'; category = 'other'; // outings/gatherings/other; chair can switch to payout
      }
    }

    const mk = memberUid ? memberUid : (reference ? 'ref:' + normRef(reference) : '');
    const dup = (() => {
      const fpKind = type === 'skip' ? 'contribution' : type;
      const fp = fingerprint(fpKind, mk, date, amount);
      const isDup = existing.has(fp) || batch.has(fp);
      batch.add(fp);
      return isDup;
    })();

    out.push({
      __i: i, date, amount, dir, description,
      type, category, loanId,
      memberKey: key || '', memberUid,
      memberName: m ? (m.name || m.email || 'Member') : '',
      reference,
      dup,
      include: !dup && type !== 'skip',
    });
  });

  return { rows: out };
}

// Re-route a row to a new type/member/category (chair override in the review UI),
// recomputing member linkage and default include state.
export function reclassifyRow(row, patch, stokvel, data) {
  const members = (stokvel && stokvel.members) || {};
  const next = { ...row, ...patch };
  const typeDef = IMPORT_TYPES.find((t) => t.id === next.type);
  if (typeDef && typeDef.member) {
    const k = next.memberKey;
    const m = k ? (members[k] || {}) : null;
    next.memberUid = k && !k.startsWith('tracked:') ? k : null;
    next.memberName = m ? (m.name || m.email || 'Member') : '';
    next.reference = m ? (m.reference || '') : '';
    if (next.type === 'repayment' && m) {
      next.loanId = activeLoanForMember((data && data.loans) || [], (data && data.repayments) || [], next.memberUid, next.reference);
    }
    // A member-linked type needs a member chosen to be includable.
    if (!k) next.include = false;
  }
  return next;
}

// Split reviewed+included rows into the buckets stokvelFeed.applyImport() expects.
export function rowsToBuckets(rows) {
  const b = { contributions: [], repayments: [], loans: [], expenses: [], income: [], payouts: [] };
  (rows || []).filter((x) => x.include && x.type !== 'skip').forEach((x) => {
    const note = (x.description || '').slice(0, 140);
    if (x.type === 'contribution') b.contributions.push({ memberUid: x.memberUid, memberName: x.memberName, reference: x.reference, date: x.date, amount: x.amount, note });
    else if (x.type === 'repayment') b.repayments.push({ loanId: x.loanId, memberUid: x.memberUid, memberName: x.memberName, date: x.date, amount: x.amount, note });
    else if (x.type === 'loanOut') b.loans.push({ memberUid: x.memberUid, memberName: x.memberName, reference: x.reference, date: x.date, amount: x.amount, interestType: x.interestType || 'none', interestRate: x.interestRate || 0, numberOfRepayments: x.numberOfRepayments || 0, note });
    else if (x.type === 'expense') b.expenses.push({ category: x.category || 'other', date: x.date, amount: x.amount, note });
    else if (x.type === 'income') b.income.push({ category: x.category || 'other', date: x.date, amount: x.amount, note });
    else if (x.type === 'payout') b.payouts.push({ memberUid: x.memberUid, memberName: x.memberName || 'Group', date: x.date, amount: x.amount, note });
  });
  return b;
}
